import { useState, useContext, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toCanvas } from 'html-to-image';
import {
  ChatDotsIcon,
  ArrowSquareOutIcon,
  CameraIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { useSubmitUiBugReport } from '@/api/rpc/hooks/core/ui-bug-report/useSubmitUiBugReport';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { HELP_MENU_ORDER, HelpMenuSlot } from '@/components/common/HelpMenu';
import { isMac } from '@/utils/platform';

type DialogState = 'idle' | 'open' | 'submitting';

type ScreenshotState =
  | { status: 'capturing' }
  | { status: 'done'; dataUrl: string }
  | { status: 'failed'; error: string };

function buildContext(currentUser: {
  id: string;
  name: string;
  emails: string[];
  organizationId: string;
  organizationName: string;
}) {
  return {
    userId: currentUser.id,
    userName: currentUser.name,
    userEmail: currentUser.emails[0] ?? null,
    organizationId: currentUser.organizationId,
    organizationName: currentUser.organizationName,
    pageUrl: window.location.href,
    pageTitle: document.title,
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    timestamp: new Date().toISOString(),
  };
}

type ScreenshotResult =
  | { ok: true; dataUrl: string }
  | { ok: false; error: string };

async function captureScreenshot(): Promise<ScreenshotResult> {
  try {
    console.log('[FeedbackButton] Starting html-to-image capture…');

    // Capture only the viewport (not the full scrollable body) and shift the
    // clone by current scroll offset so the rendered image matches what the
    // user actually sees. Cuts work dramatically on long pages.
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const canvas = await toCanvas(document.body, {
      width: viewportWidth,
      height: viewportHeight,
      // Force 1x pixel ratio — bug-report screenshots are reference shots,
      // not retina-quality assets. On high-DPI displays the default
      // `devicePixelRatio` (2–3x) inflates the canvas by 4–9x and is the
      // single biggest contributor to main-thread blocking during capture.
      pixelRatio: Math.min(window.devicePixelRatio, 1.5),
      style: {
        transform: `translate(${-scrollX}px, ${-scrollY}px)`,
        transformOrigin: 'top left',
      },
      // Skip font embedding — avoids CORS errors from Google Fonts / external
      // stylesheets whose cssRules are unreadable cross-origin in production.
      skipFonts: true,
      // Don't append `?cacheBust=…` to <img> URLs; it breaks signed/restricted
      // images and CDNs that vary on the exact URL.
      cacheBust: false,
      // 1x1 transparent placeholder used when an image's data URL fetch fails.
      imagePlaceholder:
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      // Don't reject the whole capture if a single <img> fails to load — a
      // broken icon shouldn't kill the screenshot.
      onImageErrorHandler: () => {
        // returning undefined resolves the per-image promise as-is
      },
      filter: (node) => {
        // Skip script tags to avoid serialization issues
        if (node.nodeName === 'SCRIPT') return false;
        // Skip the Radix dialog portal subtree so the feedback dialog itself
        // doesn't appear in the screenshot. The dialog is mounted by the
        // time this runs (we defer capture via rAF so the dialog feels
        // responsive). DialogPortal/Overlay/Content/Close all carry a
        // `data-slot` starting with `dialog-`, so excluding any matching
        // ancestor short-circuits the whole subtree.
        if (
          node instanceof HTMLElement &&
          node.dataset.slot?.startsWith('dialog-')
        ) {
          return false;
        }
        return true;
      },
    });

    // Async JPEG encode via `canvas.toBlob` — browsers run this off the main
    // thread, unlike the synchronous `canvas.toDataURL` path that `toJpeg`
    // uses internally. Together with `FileReader.readAsDataURL` below, this
    // keeps the textarea responsive while the screenshot finalizes.
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) =>
          b
            ? resolve(b)
            : reject(new Error('Canvas encoding produced no blob')),
        'image/jpeg',
        0.6,
      );
    });

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () =>
        reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });

    console.log(
      '[FeedbackButton] Capture done, dataUrl length:',
      dataUrl.length,
    );
    return { ok: true, dataUrl };
  } catch (err) {
    console.error('[FeedbackButton] html-to-image failed:', err);
    return { ok: false, error: describeCaptureError(err) };
  }
}

function describeCaptureError(err: unknown): string {
  if (err instanceof Error) return err.message;
  // html-to-image rejects with the raw `error` Event for image load failures —
  // `String(event)` is "[object Event]" which is useless to show the user.
  if (err instanceof Event) {
    const target = err.target;
    if (target instanceof HTMLImageElement && target.src) {
      return `Failed to load image: ${target.src}`;
    }
    return `Failed to load a page resource (${err.type})`;
  }
  if (typeof err === 'string') return err;
  return 'Unknown error while capturing the screenshot';
}

export function FeedbackButton() {
  const { currentUser } = useContext(AuthContext);
  const { mutateAsync: submitReport, isPending } = useSubmitUiBugReport();
  const gt = useGT();

  const [dialogState, setDialogState] = useState<DialogState>('idle');
  const [screenshotState, setScreenshotState] = useState<ScreenshotState>({
    status: 'capturing',
  });
  const [message, setMessage] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDialogOpen = dialogState === 'open' || dialogState === 'submitting';
  const isSubmitting = dialogState === 'submitting';
  const isCapturing = screenshotState.status === 'capturing';

  // Cmd/Ctrl+Shift+F opens the feedback dialog. react-hotkeys-hook skips key
  // events fired from inputs/textareas/contenteditable by default, so this
  // won't fire while the user is typing in a field.
  useHotkeys('mod+shift+f', () => handleOpen(), { preventDefault: true });

  function reset() {
    setDialogState('idle');
    setScreenshotState({ status: 'capturing' });
    setMessage('');
    setIncludeScreenshot(true);
  }

  function handleOpen() {
    if (!currentUser || isDialogOpen) return;

    // Open dialog immediately — capture runs in the background
    setScreenshotState({ status: 'capturing' });
    setMessage('');
    setIncludeScreenshot(true);
    setDialogState('open');

    setTimeout(() => textareaRef.current?.focus(), 50);

    // Defer capture until after the dialog has been rendered and painted.
    // Double rAF: first frame lets React flush the state update, second
    // frame lets the browser paint the dialog, then capture starts.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void captureScreenshot().then((result) => {
          setScreenshotState(
            result.ok
              ? { status: 'done', dataUrl: result.dataUrl }
              : { status: 'failed', error: result.error },
          );
        });
      });
    });
  }

  function handleClose() {
    if (isSubmitting) return;
    reset();
  }

  async function handleSubmit() {
    if (!currentUser || dialogState !== 'open') return;
    if (!message.trim()) return;

    const screenshotDataUrl =
      includeScreenshot && screenshotState.status === 'done'
        ? screenshotState.dataUrl
        : null;

    setDialogState('submitting');

    try {
      await submitReport({
        message: message.trim(),
        screenshotDataUrl,
        context: buildContext(currentUser),
      });
      reset();
    } catch {
      // Error already handled by the mutation hook toast
      setDialogState('open');
    }
  }

  if (!currentUser) return null;

  const shortcutLabel = isMac() ? '⌘⇧F' : 'Ctrl+Shift+F';

  return (
    <>
      <HelpMenuSlot order={HELP_MENU_ORDER.feedback}>
        <SidebarMenuButton
          onClick={handleOpen}
          disabled={isDialogOpen || isPending}
          className="cursor-pointer"
          aria-label={gt('Send feedback', {
            context: 'sidebar help menu button aria label for feedback',
          })}
          tooltip={gt('Send feedback ({shortcut})', {
            context:
              'tooltip on the collapsed sidebar Send feedback menu item, includes keyboard shortcut',
            shortcut: shortcutLabel,
          })}
        >
          <ChatDotsIcon />
          {/* Trailing label/shortcut block — hidden in icon-mode so the
              SidebarMenuButton can center the icon inside its 32×32 shell. */}
          <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <T context="sidebar help menu item label for sending feedback">
              Send feedback
            </T>
            <kbd className="rounded border border-sidebar-border/60 bg-sidebar-accent/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {shortcutLabel}
            </kbd>
          </span>
        </SidebarMenuButton>
      </HelpMenuSlot>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <T context="feedback dialog title">Send Feedback</T>
            </DialogTitle>
            <DialogDescription>
              <T context="feedback dialog subtitle">
                Report an issue or share feedback with the support team.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="feedback-message" className="mb-1">
                <T context="label above feedback text input">
                  What&apos;s the issue or feedback?
                </T>
              </Label>
              <Textarea
                id="feedback-message"
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={gt(
                  "Describe what happened or what you'd like to see improved\u2026",
                  {
                    context: 'placeholder text in the feedback textarea',
                  },
                )}
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <div className="rounded-md border bg-muted/40 p-3 flex flex-col gap-2">
              <div className="flex items-start gap-3">
                {/* Thumbnail / spinner */}
                {screenshotState.status === 'capturing' && (
                  <div className="shrink-0 h-16 w-24 rounded border bg-muted flex items-center justify-center">
                    <SpinnerIcon className="h-5 w-5 text-muted-foreground animate-spin" />
                  </div>
                )}
                {screenshotState.status === 'done' && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // data: URLs are blocked in new tabs by Chrome — use a blob URL instead
                      const byteString = atob(
                        screenshotState.dataUrl.split(',')[1] ?? '',
                      );
                      const bytes = new Uint8Array(byteString.length);
                      for (let i = 0; i < byteString.length; i++) {
                        bytes[i] = byteString.charCodeAt(i);
                      }
                      const blob = new Blob([bytes], { type: 'image/jpeg' });
                      const blobUrl = URL.createObjectURL(blob);
                      const tab = window.open(blobUrl, '_blank');
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
                      tab?.focus();
                    }}
                    rel="noopener noreferrer"
                    className="shrink-0 group relative cursor-pointer"
                    title="Open screenshot in new tab"
                  >
                    <img
                      src={screenshotState.dataUrl}
                      alt={gt('Page screenshot', {
                        context:
                          'alt text for screenshot thumbnail in feedback dialog',
                      })}
                      className={`h-16 w-24 rounded border object-cover transition-opacity ${includeScreenshot ? '' : 'opacity-40'}`}
                    />
                    <span className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                      <ArrowSquareOutIcon
                        className="h-4 w-4 text-white"
                        weight="bold"
                      />
                    </span>
                  </a>
                )}
                {screenshotState.status === 'failed' && (
                  <div className="shrink-0 h-16 w-24 rounded border bg-muted flex items-center justify-center">
                    <CameraIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}

                {/* Text */}
                <div className="flex flex-col gap-1 min-w-0">
                  {screenshotState.status === 'capturing' ? (
                    <p className="text-xs text-muted-foreground">
                      <T context="message shown while screenshot is being captured in the feedback dialog">
                        Capturing screenshot…
                      </T>
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                        <CameraIcon
                          className="h-3.5 w-3.5 shrink-0"
                          weight="fill"
                        />
                        {screenshotState.status === 'done' ? (
                          <T context="heading shown when a screenshot was successfully captured">
                            Screenshot captured
                          </T>
                        ) : (
                          <T context="heading shown when screenshot capture failed">
                            Screenshot unavailable
                          </T>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {screenshotState.status === 'done' ? (
                          <T context="info message in feedback dialog explaining screenshot will be sent to support">
                            A screenshot of your current page will be shared
                            with the support team to help diagnose the issue.
                          </T>
                        ) : (
                          <T context="info message in feedback dialog when screenshot capture failed">
                            Could not capture a screenshot automatically.
                          </T>
                        )}
                      </p>
                      {screenshotState.status === 'failed' && (
                        <p className="text-xs text-destructive font-mono break-all">
                          {screenshotState.error}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {screenshotState.status === 'done' && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    id="include-screenshot"
                    checked={includeScreenshot}
                    onCheckedChange={(checked) =>
                      setIncludeScreenshot(checked === true)
                    }
                    disabled={isSubmitting}
                  />
                  <span className="text-xs text-muted-foreground">
                    <T context="checkbox label to opt out of including a screenshot in the feedback report">
                      Include screenshot with this report
                    </T>
                  </span>
                </label>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <T context="cancel button in feedback dialog">Cancel</T>
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!message.trim() || isSubmitting || isCapturing}
            >
              {isSubmitting ? (
                <T context="feedback submit button label while sending is in progress">
                  Sending…
                </T>
              ) : (
                <T context="feedback submit button label">Send feedback</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
